import { useEffect, useMemo, useState } from "react";
import { getDownloadURL, ref, uploadString } from "firebase/storage";
import { doc, increment, serverTimestamp, setDoc } from "firebase/firestore";
import { db, storage } from "../firebase";

const CSV_FILES = [
  {
    id: "ui",
    label: "UI General",
    path: "UI/Localization/UIText.csv",
  },
  {
    id: "minigames",
    label: "Minijuegos",
    path: "UI/Localization/MinigamesText.csv",
  },
  {
    id: "missions",
    label: "Misiones",
    path: "UI/Localization/MissionsText.csv",
  },
];

const DEFAULT_HEADERS = ["Key", "Spanish(es)", "English(en)"];

function getRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function cleanText(text) {
  return String(text || "")
    .replace(/\uFEFF/g, "")
    .replace(/\u0000/g, "")
    .replace(/\u200B/g, "")
    .replace(/\u200C/g, "")
    .replace(/\u200D/g, "");
}

function cleanCell(value) {
  return String(value ?? "")
    .replace(/\uFEFF/g, "")
    .replace(/\u0000/g, "")
    .replace(/\u200B/g, "")
    .replace(/\u200C/g, "")
    .replace(/\u200D/g, "")
    .replace(/\u00A0/g, " ");
}

function cleanKey(value) {
  return cleanCell(value).trim();
}

function detectCSVSeparator(text) {
  const clean = cleanText(text);
  const firstLine = clean.split(/\r?\n/).find((line) => line.trim()) || "";

  const commaCount = (firstLine.match(/,/g) || []).length;
  const semicolonCount = (firstLine.match(/;/g) || []).length;
  const tabCount = (firstLine.match(/\t/g) || []).length;

  if (tabCount > commaCount && tabCount > semicolonCount) return "\t";
  if (semicolonCount > commaCount) return ";";

  return ",";
}

function parseCSV(text) {
  const rows = [];
  let currentRow = [];
  let currentValue = "";
  let insideQuotes = false;

  const clean = cleanText(text);
  const separator = detectCSVSeparator(clean);

  for (let i = 0; i < clean.length; i++) {
    const char = clean[i];
    const nextChar = clean[i + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      currentValue += '"';
      i++;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === separator && !insideQuotes) {
      currentRow.push(currentValue);
      currentValue = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (char === "\r" && nextChar === "\n") {
        i++;
      }

      currentRow.push(currentValue);

      if (currentRow.some((cell) => cleanCell(cell).trim() !== "")) {
        rows.push(currentRow);
      }

      currentRow = [];
      currentValue = "";
      continue;
    }

    currentValue += char;
  }

  if (currentValue !== "" || currentRow.length > 0) {
    currentRow.push(currentValue);

    if (currentRow.some((cell) => cleanCell(cell).trim() !== "")) {
      rows.push(currentRow);
    }
  }

  return {
    rows,
    separator,
  };
}

function normalizeHeader(header) {
  const cleaned = cleanCell(header).trim();

  if (cleaned.toLowerCase() === "key") return "Key";

  return cleaned;
}

function rowsToObjects(parsedRows) {
  if (!parsedRows.length) {
    return {
      headers: DEFAULT_HEADERS,
      rows: [],
      emptyKeyRows: 0,
      skippedEmptyRows: 0,
    };
  }

  const headers = parsedRows[0].map(normalizeHeader);

  const keyIndex = headers.findIndex(
    (header) => cleanCell(header).trim().toLowerCase() === "key"
  );

  if (keyIndex === -1) {
    throw new Error(
      `El CSV no tiene columna Key. Headers encontrados: ${headers.join(", ")}`
    );
  }

  const normalizedHeaders = headers.map((header, index) =>
    index === keyIndex ? "Key" : header
  );

  let emptyKeyRows = 0;
  let skippedEmptyRows = 0;

  const rows = [];

  parsedRows.slice(1).forEach((row, index) => {
    const objectRow = {
      _rowId: getRandomId(),
      _originalIndex: index,
    };

    normalizedHeaders.forEach((header, headerIndex) => {
      objectRow[header] = cleanCell(row[headerIndex] ?? "");
    });

    objectRow.Key = cleanKey(objectRow.Key);

    const hasAnyContent = normalizedHeaders.some((header) => {
      return cleanCell(objectRow[header]).trim() !== "";
    });

    if (!hasAnyContent) {
      skippedEmptyRows++;
      return;
    }

    if (!objectRow.Key) {
      emptyKeyRows++;
    }

    rows.push(objectRow);
  });

  return {
    headers: normalizedHeaders,
    rows,
    emptyKeyRows,
    skippedEmptyRows,
  };
}

function escapeCSVValue(value) {
  const stringValue = String(value ?? "");

  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

function buildCSV(headers, rows) {
  const lines = [];

  lines.push(headers.map(escapeCSVValue).join(","));

  rows.forEach((row) => {
    lines.push(
      headers.map((header) => escapeCSVValue(row[header] ?? "")).join(",")
    );
  });

  return lines.join("\n");
}

function createEmptyRow(headers, key = "") {
  const row = {
    _rowId: getRandomId(),
  };

  headers.forEach((header) => {
    row[header] = "";
  });

  row.Key = key;

  return row;
}

function getFriendlyStorageError(err, selectedFilePath) {
  const code = err?.code;
  const message = err?.message || String(err);

  if (code === "storage/retry-limit-exceeded") {
    return `No se pudo cargar el CSV: ${selectedFilePath}. La descarga agotó los reintentos. Revisa CORS, conexión, reglas de Storage y que el archivo exista.`;
  }

  if (code === "storage/unauthorized") {
    return `No se pudo cargar el CSV: ${selectedFilePath}. No tienes permisos para leer este archivo. Revisa las reglas de Firebase Storage.`;
  }

  if (code === "storage/object-not-found") {
    return `No se pudo cargar el CSV: ${selectedFilePath}. El archivo no existe en esa ruta. Revisa mayúsculas, minúsculas, carpeta y nombre exacto.`;
  }

  if (code === "storage/quota-exceeded") {
    return `No se pudo cargar el CSV: ${selectedFilePath}. Se excedió la cuota del bucket.`;
  }

  if (code === "storage/canceled") {
    return `No se pudo cargar el CSV: ${selectedFilePath}. La operación fue cancelada.`;
  }

  return `No se pudo cargar el CSV: ${selectedFilePath}. Error: ${code || message}`;
}

function validateRowsBeforeSave(headers, rows) {
  if (!headers.includes("Key")) {
    return "No existe la columna Key.";
  }

  const emptyKeys = rows.filter((row) => !cleanKey(row.Key));

  if (emptyKeys.length > 0) {
    return `Hay ${emptyKeys.length} fila(s) con Key vacía. Corrige o elimina esas filas antes de guardar.`;
  }

  const keys = rows.map((row) => cleanKey(row.Key));

  const duplicatedKeys = keys.filter(
    (key, index, array) => key && array.indexOf(key) !== index
  );

  if (duplicatedKeys.length > 0) {
    return `Hay keys duplicadas: ${[...new Set(duplicatedKeys)].join(", ")}`;
  }

  const duplicatedHeaders = headers.filter(
    (header, index, array) =>
      header &&
      array.findIndex(
        (item) => item.trim().toLowerCase() === header.trim().toLowerCase()
      ) !== index
  );

  if (duplicatedHeaders.length > 0) {
    return `Hay columnas duplicadas: ${[...new Set(duplicatedHeaders)].join(", ")}`;
  }

  return "";
}

function LocalizationPage() {
  const [selectedFilePath, setSelectedFilePath] = useState(CSV_FILES[0].path);

  const [headers, setHeaders] = useState(DEFAULT_HEADERS);
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [newRow, setNewRow] = useState(() => createEmptyRow(DEFAULT_HEADERS));
  const [newLanguageHeader, setNewLanguageHeader] = useState("");

  const [bulkCSVText, setBulkCSVText] = useState("");
  const [bulkReplaceExisting, setBulkReplaceExisting] = useState(false);

  const [error, setError] = useState("");
  const [debugInfo, setDebugInfo] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const selectedFile = CSV_FILES.find((file) => file.path === selectedFilePath);

  const languageHeaders = headers.filter((header) => header !== "Key");

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) return rows;

    return rows.filter((row) =>
      headers.some((header) =>
        String(row[header] || "").toLowerCase().includes(normalizedSearch)
      )
    );
  }, [rows, headers, searchText]);

  async function loadCSV() {
    try {
      if (hasUnsavedChanges) {
        const confirmReload = window.confirm(
          "Tienes cambios sin guardar. Si recargas, se perderán. ¿Quieres continuar?"
        );

        if (!confirmReload) return;
      }

      setLoading(true);
      setError("");
      setDebugInfo("");
      setSearchText("");

      const fileRef = ref(storage, selectedFilePath);
      const downloadUrl = await getDownloadURL(fileRef);

      const separator = downloadUrl.includes("?") ? "&" : "?";
      const response = await fetch(
        `${downloadUrl}${separator}cacheBust=${Date.now()}`,
        {
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = cleanText(await response.text());

      if (!text.trim()) {
        throw new Error("El archivo CSV está vacío.");
      }

      const parsed = parseCSV(text);
      const result = rowsToObjects(parsed.rows);

      setHeaders(result.headers);
      setRows(result.rows);
      setNewRow(createEmptyRow(result.headers));
      setNewLanguageHeader("");
      setHasUnsavedChanges(false);

      const rawPreview = text.slice(0, 180).replace(/\n/g, "\\n");

      console.log("[LocalizationPage] Archivo:", selectedFilePath);
      console.log("[LocalizationPage] Separador detectado:", parsed.separator);
      console.log("[LocalizationPage] CSV RAW preview:", rawPreview);
      console.log("[LocalizationPage] Parsed rows:", parsed.rows);
      console.log("[LocalizationPage] Result rows:", result.rows);

      let info = `Archivo cargado: ${selectedFilePath} · Filas visibles: ${result.rows.length} · Columnas: ${result.headers.length} · Separador: ${parsed.separator === "\t" ? "TAB" : parsed.separator
        } · Filas parseadas: ${parsed.rows.length}`;

      if (result.emptyKeyRows > 0) {
        info += ` · Filas con Key vacía: ${result.emptyKeyRows}`;
      }

      if (result.skippedEmptyRows > 0) {
        info += ` · Filas vacías omitidas: ${result.skippedEmptyRows}`;
      }

      info += ` · Preview: ${rawPreview}`;

      setDebugInfo(info);
    } catch (err) {
      console.error("Error cargando CSV:", err);

      setError(getFriendlyStorageError(err, selectedFilePath));

      setHeaders(DEFAULT_HEADERS);
      setRows([]);
      setNewRow(createEmptyRow(DEFAULT_HEADERS));
      setHasUnsavedChanges(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCSV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilePath]);

  function markAsChanged() {
    setHasUnsavedChanges(true);
  }

  function updateRow(rowId, field, value) {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row._rowId === rowId
          ? {
            ...row,
            [field]: field === "Key" ? cleanCell(value).trimStart() : value,
          }
          : row
      )
    );

    markAsChanged();
  }

  function updateNewRow(field, value) {
    setNewRow((prev) => ({
      ...prev,
      [field]: field === "Key" ? cleanCell(value).trimStart() : value,
    }));
  }

  function moveRow(rowId, direction) {
    setRows((prevRows) => {
      const currentIndex = prevRows.findIndex((row) => row._rowId === rowId);

      if (currentIndex === -1) return prevRows;

      const nextIndex = currentIndex + direction;

      if (nextIndex < 0 || nextIndex >= prevRows.length) {
        return prevRows;
      }

      const copy = [...prevRows];
      const [removed] = copy.splice(currentIndex, 1);
      copy.splice(nextIndex, 0, removed);

      return copy;
    });

    markAsChanged();
  }

  function deleteRow(rowId) {
    const row = rows.find((item) => item._rowId === rowId);

    const confirmDelete = window.confirm(
      `¿Seguro que quieres eliminar la key "${row?.Key || ""}"?`
    );

    if (!confirmDelete) return;

    setRows((prevRows) => prevRows.filter((item) => item._rowId !== rowId));
    markAsChanged();
  }

  function addRow() {
    const key = cleanKey(newRow.Key);

    if (!key) {
      alert("Ingresa una Key.");
      return;
    }

    const exists = rows.some(
      (row) => cleanKey(row.Key).toLowerCase() === key.toLowerCase()
    );

    if (exists) {
      alert("Esa Key ya existe.");
      return;
    }

    const rowToAdd = {
      ...createEmptyRow(headers),
      ...newRow,
      _rowId: getRandomId(),
      Key: key,
    };

    setRows((prevRows) => [...prevRows, rowToAdd]);
    setNewRow(createEmptyRow(headers));
    markAsChanged();
  }

  function looksLikeHeaderRow(row) {
    if (!row || row.length === 0) return false;

    return cleanCell(row[0]).trim().toLowerCase() === "key";
  }

  function findHeaderIndex(sourceHeaders, targetHeader) {
    const normalizedTarget = cleanCell(targetHeader).trim().toLowerCase();

    return sourceHeaders.findIndex(
      (header) => cleanCell(header).trim().toLowerCase() === normalizedTarget
    );
  }

  function buildBulkRowFromParsedRow(parsedRow, sourceHeaders, hasHeader) {
    const row = createEmptyRow(headers);

    headers.forEach((header, headerIndex) => {
      let value = "";

      if (hasHeader) {
        const sourceIndex = findHeaderIndex(sourceHeaders, header);
        value = sourceIndex >= 0 ? parsedRow[sourceIndex] ?? "" : "";
      } else {
        value = parsedRow[headerIndex] ?? "";
      }

      row[header] = header === "Key" ? cleanKey(value) : cleanCell(value);
    });

    row._rowId = getRandomId();

    return row;
  }

  function addBulkRows() {
    const text = cleanText(bulkCSVText);

    if (!text.trim()) {
      alert("Pega al menos una línea CSV.");
      return;
    }

    let parsed;

    try {
      parsed = parseCSV(text);
    } catch (err) {
      console.error("Error parseando líneas:", err);
      alert("No se pudieron leer las líneas. Revisa el formato CSV.");
      return;
    }

    if (!parsed.rows.length) {
      alert("No se encontraron filas válidas.");
      return;
    }

    const hasHeader = looksLikeHeaderRow(parsed.rows[0]);
    const sourceHeaders = hasHeader
      ? parsed.rows[0].map(normalizeHeader)
      : headers;

    const dataRows = hasHeader ? parsed.rows.slice(1) : parsed.rows;

    if (!dataRows.length) {
      alert("El texto solo tiene headers, no hay filas para agregar.");
      return;
    }

    const parsedObjects = [];
    const emptyKeyRows = [];

    dataRows.forEach((parsedRow, index) => {
      const row = buildBulkRowFromParsedRow(parsedRow, sourceHeaders, hasHeader);

      const hasAnyContent = headers.some(
        (header) => cleanCell(row[header]).trim() !== ""
      );

      if (!hasAnyContent) return;

      if (!row.Key) {
        emptyKeyRows.push(index + 1);
        return;
      }

      parsedObjects.push(row);
    });

    if (emptyKeyRows.length > 0) {
      alert(
        `Hay ${emptyKeyRows.length} fila(s) sin Key. Corrige esas líneas antes de agregarlas.`
      );
      return;
    }

    if (!parsedObjects.length) {
      alert("No hay filas válidas para agregar.");
      return;
    }

    const pastedKeys = parsedObjects.map((row) => cleanKey(row.Key).toLowerCase());

    const duplicatedInsidePaste = pastedKeys.filter(
      (key, index, array) => key && array.indexOf(key) !== index
    );

    if (duplicatedInsidePaste.length > 0) {
      alert(
        `Hay keys duplicadas dentro del texto pegado: ${[
          ...new Set(duplicatedInsidePaste),
        ].join(", ")}`
      );
      return;
    }

    const existingKeyMap = new Map(
      rows.map((row) => [cleanKey(row.Key).toLowerCase(), row])
    );

    const duplicatedExisting = parsedObjects.filter((row) =>
      existingKeyMap.has(cleanKey(row.Key).toLowerCase())
    );

    if (duplicatedExisting.length > 0 && !bulkReplaceExisting) {
      alert(
        `Estas keys ya existen: ${duplicatedExisting
          .map((row) => row.Key)
          .join(", ")}. Activa "reemplazar existentes" si quieres actualizarlas.`
      );
      return;
    }

    if (bulkReplaceExisting) {
      setRows((prevRows) => {
        const incomingMap = new Map(
          parsedObjects.map((row) => [cleanKey(row.Key).toLowerCase(), row])
        );

        const updatedRows = prevRows.map((row) => {
          const key = cleanKey(row.Key).toLowerCase();
          const incoming = incomingMap.get(key);

          if (!incoming) return row;

          incomingMap.delete(key);

          return {
            ...row,
            ...incoming,
            _rowId: row._rowId,
          };
        });

        return [...updatedRows, ...Array.from(incomingMap.values())];
      });
    } else {
      setRows((prevRows) => [...prevRows, ...parsedObjects]);
    }

    setBulkCSVText("");
    markAsChanged();

    alert(
      `Se agregaron ${parsedObjects.length} línea(s)${bulkReplaceExisting ? " / actualizadas si ya existían" : ""
      }.`
    );
  }

  function addLanguageColumn() {
    const header = newLanguageHeader.trim();

    if (!header) {
      alert("Ingresa el nombre de la columna. Ejemplo: French(fr)");
      return;
    }

    if (header.toLowerCase() === "key") {
      alert("No puedes crear otra columna Key.");
      return;
    }

    if (header.includes(",")) {
      alert("Evita usar comas en el nombre de la columna.");
      return;
    }

    const exists = headers.some(
      (item) => item.trim().toLowerCase() === header.toLowerCase()
    );

    if (exists) {
      alert("Esa columna ya existe.");
      return;
    }

    setHeaders((prevHeaders) => [...prevHeaders, header]);

    setRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        [header]: "",
      }))
    );

    setNewRow((prev) => ({
      ...prev,
      [header]: "",
    }));

    setNewLanguageHeader("");
    markAsChanged();
  }

  function deleteLanguageColumn(headerToDelete) {
    if (headerToDelete === "Key") return;

    const confirmDelete = window.confirm(
      `¿Seguro que quieres eliminar la columna "${headerToDelete}"? También se perderán sus textos.`
    );

    if (!confirmDelete) return;

    setHeaders((prevHeaders) =>
      prevHeaders.filter((header) => header !== headerToDelete)
    );

    setRows((prevRows) =>
      prevRows.map((row) => {
        const copy = { ...row };
        delete copy[headerToDelete];
        return copy;
      })
    );

    setNewRow((prev) => {
      const copy = { ...prev };
      delete copy[headerToDelete];
      return copy;
    });

    markAsChanged();
  }

  function renameLanguageColumn(oldHeader) {
    const newHeader = window.prompt("Nuevo nombre de la columna:", oldHeader);

    if (newHeader === null) return;

    const cleanNewHeader = newHeader.trim();

    if (!cleanNewHeader) {
      alert("El nombre no puede estar vacío.");
      return;
    }

    if (cleanNewHeader.toLowerCase() === "key") {
      alert("No puedes usar Key como columna de idioma.");
      return;
    }

    if (cleanNewHeader.includes(",")) {
      alert("Evita usar comas en el nombre de la columna.");
      return;
    }

    if (cleanNewHeader === oldHeader) return;

    const exists = headers.some(
      (header) =>
        header !== oldHeader &&
        header.trim().toLowerCase() === cleanNewHeader.toLowerCase()
    );

    if (exists) {
      alert("Ya existe una columna con ese nombre.");
      return;
    }

    setHeaders((prevHeaders) =>
      prevHeaders.map((header) =>
        header === oldHeader ? cleanNewHeader : header
      )
    );

    setRows((prevRows) =>
      prevRows.map((row) => {
        const copy = { ...row };
        copy[cleanNewHeader] = copy[oldHeader] ?? "";
        delete copy[oldHeader];
        return copy;
      })
    );

    setNewRow((prev) => {
      const copy = { ...prev };
      copy[cleanNewHeader] = copy[oldHeader] ?? "";
      delete copy[oldHeader];
      return copy;
    });

    markAsChanged();
  }

  async function updateLocalizationVersion(file) {
    if (!file?.id || !file?.path) {
      throw new Error("No se pudo identificar el archivo de localización.");
    }

    const configRef = doc(db, "config", "game");

    await setDoc(
      configRef,
      {
        localization: {
          [file.id]: {
            path: file.path,
            version: increment(1),
            updatedAt: serverTimestamp(),
          },
        },
      },
      { merge: true }
    );
  }

  async function saveCSV() {
    try {
      setSaving(true);
      setError("");

      if (rows.length === 0) {
        const confirmEmptySave = window.confirm(
          "El CSV no tiene filas. Si guardas ahora, subirás un archivo solo con headers. ¿Seguro que quieres continuar?"
        );

        if (!confirmEmptySave) return;
      }

      const validationError = validateRowsBeforeSave(headers, rows);

      if (validationError) {
        alert(validationError);
        return;
      }

      const cleanRows = rows.map((row) => {
        const cleanRow = {};

        headers.forEach((header) => {
          if (header === "Key") {
            cleanRow[header] = cleanKey(row[header]);
          } else {
            cleanRow[header] = row[header] ?? "";
          }
        });

        return cleanRow;
      });

      const csvText = buildCSV(headers, cleanRows);
      const fileRef = ref(storage, selectedFilePath);

      await uploadString(fileRef, csvText, "raw", {
        contentType: "text/csv;charset=utf-8",
        cacheControl: "no-cache, no-store, must-revalidate",
      });

      await updateLocalizationVersion(selectedFile);

      setHasUnsavedChanges(false);

      setDebugInfo(
        `Archivo guardado: ${selectedFilePath} · Filas: ${cleanRows.length} · Columnas: ${headers.length} · Versión incrementada en Firestore.`
      );

      alert("CSV guardado correctamente.");

      await loadCSV();
    } catch (err) {
      console.error("Error guardando CSV:", err);

      setError(`No se pudo guardar el CSV. Error: ${err.code || err.message}`);
    } finally {
      setSaving(false);
    }
  }

  function downloadLocalCSV() {
    const validationError = validateRowsBeforeSave(headers, rows);

    if (validationError) {
      alert(validationError);
      return;
    }

    const cleanRows = rows.map((row) => {
      const cleanRow = {};

      headers.forEach((header) => {
        cleanRow[header] = row[header] ?? "";
      });

      return cleanRow;
    });

    const csvText = buildCSV(headers, cleanRows);
    const blob = new Blob([csvText], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    const fileName = selectedFilePath.split("/").pop() || "localization.csv";

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  }

  return (
    <section className="admin-section-card">
      <div className="section-header">
        <div>
          <h2>Localización</h2>
          <p>
            Edita el CSV desde el panel. Los cambios solo se suben a Firebase
            cuando presionas Guardar CSV.
          </p>

          {hasUnsavedChanges && (
            <p className="field-hint">Hay cambios sin guardar.</p>
          )}
        </div>

        <div className="section-actions">
          <button
            className="primary-action-button"
            onClick={saveCSV}
            disabled={saving || loading}
          >
            {saving ? "Guardando..." : "Guardar CSV"}
          </button>

          <button
            className="secondary-button"
            onClick={loadCSV}
            disabled={saving || loading}
          >
            {loading ? "Cargando..." : "Recargar"}
          </button>

          <button
            className="secondary-button"
            onClick={downloadLocalCSV}
            disabled={saving || loading}
          >
            Descargar CSV
          </button>
        </div>
      </div>

      <div className="localization-toolbar">
        <label>
          Archivo
          <select
            value={selectedFilePath}
            onChange={(e) => {
              const nextPath = e.target.value;

              if (hasUnsavedChanges) {
                const confirmChange = window.confirm(
                  "Tienes cambios sin guardar. Si cambias de archivo, se perderán. ¿Quieres continuar?"
                );

                if (!confirmChange) return;
              }

              setSelectedFilePath(nextPath);
            }}
            disabled={saving || loading}
          >
            {CSV_FILES.map((file) => (
              <option key={file.path} value={file.path}>
                {file.label} — {file.path}
              </option>
            ))}
          </select>
        </label>

        <div className="search-box">
          <span>🔎</span>
          <input
            type="text"
            placeholder="Buscar key o texto..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            disabled={loading}
          />
        </div>
      </div>

      <div className="csv-file-card">
        <strong>{selectedFile?.label}</strong>
        <span>{selectedFilePath}</span>
      </div>

      <div className="add-language-card">
        <div className="form-block-title">
          <h3>Columnas de idiomas</h3>
          <p>
            Añade un idioma nuevo sin guardar todavía en Firebase. Se guardará
            cuando presiones Guardar CSV.
          </p>
        </div>

        <div className="add-language-row">
          <input
            type="text"
            placeholder="Ejemplo: French(fr)"
            value={newLanguageHeader}
            onChange={(e) => setNewLanguageHeader(e.target.value)}
            disabled={saving || loading}
          />

          <button
            type="button"
            className="secondary-button"
            onClick={addLanguageColumn}
            disabled={saving || loading}
          >
            Añadir idioma
          </button>
        </div>

        {languageHeaders.length > 0 && (
          <div className="language-columns-list">
            {languageHeaders.map((header) => (
              <div className="language-column-pill" key={header}>
                <span>{header}</span>

                <button
                  type="button"
                  className="small-button"
                  onClick={() => renameLanguageColumn(header)}
                  disabled={saving || loading}
                >
                  Renombrar
                </button>

                <button
                  type="button"
                  className="small-button small-button--danger"
                  onClick={() => deleteLanguageColumn(header)}
                  disabled={saving || loading}
                >
                  Eliminar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/*debugInfo && <p className="field-hint">{debugInfo}</p>*/}

      {error && <p className="dashboard-error">{error}</p>}

      {loading ? (
        <p>Cargando CSV...</p>
      ) : (
        <>
          <div className="localization-list">
            {filteredRows.map((row) => {
              const realIndex = rows.findIndex(
                (item) => item._rowId === row._rowId
              );

              return (
                <article className="localization-row-card" key={row._rowId}>
                  <div className="localization-row-card__header">
                    <div>
                      <span className="localization-row-card__index">
                        #{realIndex + 1}
                      </span>

                      <strong>
                        {row.Key || "Sin key - corrige antes de guardar"}
                      </strong>
                    </div>

                    <div className="table-actions">
                      <button
                        type="button"
                        className="small-button"
                        onClick={() => moveRow(row._rowId, -1)}
                        disabled={saving || rows[0]?._rowId === row._rowId}
                      >
                        ↑
                      </button>

                      <button
                        type="button"
                        className="small-button"
                        onClick={() => moveRow(row._rowId, 1)}
                        disabled={
                          saving ||
                          rows[rows.length - 1]?._rowId === row._rowId
                        }
                      >
                        ↓
                      </button>

                      <button
                        type="button"
                        className="small-button small-button--danger"
                        onClick={() => deleteRow(row._rowId)}
                        disabled={saving}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>

                  <div className="localization-fields">
                    <label>
                      Key
                      <input
                        type="text"
                        value={row.Key || ""}
                        onChange={(e) =>
                          updateRow(row._rowId, "Key", e.target.value)
                        }
                        disabled={saving}
                      />
                    </label>

                    {languageHeaders.map((header) => (
                      <label key={`${row._rowId}-${header}`}>
                        {header}
                        <textarea
                          value={row[header] || ""}
                          onChange={(e) =>
                            updateRow(row._rowId, header, e.target.value)
                          }
                          rows={2}
                          disabled={saving}
                        />
                      </label>
                    ))}
                  </div>
                </article>
              );
            })}

            {filteredRows.length === 0 && (
              <p className="empty-state">
                No hay textos para mostrar. Revisa el Preview del debug arriba:
                si ahí aparece contenido, el problema está en el formato de las
                filas o en la columna Key.
              </p>
            )}
          </div>

          <div className="add-localization-card">
            <div className="form-block-title">
              <h3>Añadir nueva línea</h3>
              <p>Se agregará al final del CSV.</p>
            </div>

            <div className="localization-fields">
              <label>
                Key
                <input
                  type="text"
                  placeholder="key_example"
                  value={newRow.Key || ""}
                  onChange={(e) => updateNewRow("Key", e.target.value)}
                  disabled={saving}
                />
              </label>

              {languageHeaders.map((header) => (
                <label key={`new-${header}`}>
                  {header}
                  <textarea
                    value={newRow[header] || ""}
                    onChange={(e) => updateNewRow(header, e.target.value)}
                    rows={2}
                    disabled={saving}
                  />
                </label>
              ))}
            </div>

            <div className="edit-modal__actions">
              <button
                type="button"
                className="primary-action-button"
                onClick={addRow}
                disabled={saving}
              >
                Añadir línea
              </button>
            </div>
          </div>

          <div className="add-localization-card">
            <div className="form-block-title">
              <h3>Añadir varias líneas</h3>
              <p>
                Pega varias filas en formato CSV. Puedes pegar solo filas o incluir el
                header Key, Spanish(es), English(en).
              </p>
            </div>

            <label>
              Líneas CSV
              <textarea
                value={bulkCSVText}
                onChange={(e) => setBulkCSVText(e.target.value)}
                rows={8}
                placeholder={`mission_auto_play_title,Juega {amount} partidas,Play {amount} games
mission_auto_play_desc,Completa {amount} partidas en {minigame},Complete {amount} games in {minigame}`}
                disabled={saving || loading}
              />
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={bulkReplaceExisting}
                onChange={(e) => setBulkReplaceExisting(e.target.checked)}
                disabled={saving || loading}
              />
              Reemplazar textos si la Key ya existe
            </label>

            <div className="edit-modal__actions">
              <button
                type="button"
                className="primary-action-button"
                onClick={addBulkRows}
                disabled={saving || loading || !bulkCSVText.trim()}
              >
                Añadir varias líneas
              </button>

              <button
                type="button"
                className="secondary-button"
                onClick={() => setBulkCSVText("")}
                disabled={saving || loading || !bulkCSVText.trim()}
              >
                Limpiar
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default LocalizationPage;